/**
 * Data-residency region for the document viewer (AU DR).
 *
 * Recipient links generated by the proposals backend on a non-US cell carry `?region=au`.
 * When that param is present the viewer must talk to that region's backend, which it signals
 * with the `x-dr-region` header.
 *
 * No param (or `region=us`) is the default US behaviour: no header is sent and nothing changes.
 */

export const DR_REGION_QUERY_PARAM = 'region'
export const DR_REGION_HEADER = 'x-dr-region'

/**
 * Region code for this session. '' for US / missing, i.e. the default case.
 *
 * The first non-US value seen in the url is remembered, so in-app navigations that drop the
 * query param (decline-success, error pages) keep talking to the same regional backend.
 */
let cachedRegion = ''

export const getDrRegion = (): string => {
  try {
    const region = new URLSearchParams(window.location.search).get(DR_REGION_QUERY_PARAM)?.trim().toLowerCase()
    if (region && region !== 'us') {
      cachedRegion = region
    }
  } catch (e) {
    // window/URLSearchParams unavailable - fall through to the cached value.
  }
  return cachedRegion
}

/** `{ 'x-dr-region': 'au' }` on a non-US link, `{}` otherwise — safe to spread into any headers. */
export const getDrRegionHeader = (): Record<string, string> => {
  const region = getDrRegion()
  return region ? { [DR_REGION_HEADER]: region } : {}
}

/** `&region=au` on a non-US link, '' otherwise — for urls that already carry a query string. */
export const getDrRegionQuerySuffix = (): string => {
  const region = getDrRegion()
  return region ? `&${DR_REGION_QUERY_PARAM}=${region}` : ''
}
